import React from 'react';
import { tx, tr } from '../utils/i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const ServicesSection = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const servicesData = [
    {
      id: 1,
      title: t('msnTitle'),
      description: t('msnDescription'),
      icon: "Monitor",
      image: "https://images.unsplash.com/photo-1533069027836-fa937181a8ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxNaWNyb3NvZnQlMjBhZHZlcnRpc2luZ3xlbnwwfHx8Ymx1ZXwxNzYxNTc3Mjg5fDA&ixlib=rb-4.1.0&q=85",
      link: "/services/msn-microsoft-network",
      features: ["Outlook Desktop & Mobile", "Bing Search & Shopping", "Edge Tiles & Shopping"]
    },
    {
      id: 2,
      title: t('couponTitle'),
      description: t('couponDescription'),
      icon: "Tag",
      image: "https://images.pexels.com/photos/7563655/pexels-photo-7563655.jpeg",
      link: "/services/coupon-deals",
      features: [
        tx(currentLanguage, "Sites de coupons premium", "Premium coupon sites", "Premium-Gutschein-Sites"), 
        tx(currentLanguage, "Audiences haute intention", "High-intent audiences", "Hochintentive Zielgruppen"), 
        tx(currentLanguage, "Placement brand-safe", "Brand-safe placement", "Markensichere Platzierung")
      ]
    },
    {
      id: 3,
      title: t('commerceTitle'),
      description: t('commerceDescription'),
      icon: "FileText",
      image: "https://images.unsplash.com/photo-1576864333214-b2cee50f8283?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxuYXRpdmUlMjBhZHZlcnRpc2luZ3xlbnwwfHx8Ymx1ZXwxNzYxNTc3MzAxfDA&ixlib=rb-4.1.0&q=85",
      link: "/services/commerce-content",
      features: [
        tx(currentLanguage, "Contenu contextuel", "Contextual content", "Kontextueller Inhalt"), 
        tx(currentLanguage, "Audiences niche", "Niche audiences", "Nischen-Zielgruppen"), 
        tx(currentLanguage, "Intégration native", "Native integration", "Native Integration")
      ]
    },
    {
      id: 4,
      title: t('bnplTitle'),
      description: t('bnplDescription'),
      icon: "CreditCard",
      image: "https://images.unsplash.com/photo-1648161235886-32b6764917e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmaW50ZWNoJTIwcGF5bWVudHxlbnwwfHx8Ymx1ZXwxNzYxNTc3MzA2fDA&ixlib=rb-4.1.0&q=85",
      link: "/services/bnpl",
      features: ["Klarna, Afterpay, ZIP", "AOV élevés", 
        tx(currentLanguage, "Audiences qualifiées", "Qualified audiences", "Qualifizierte Zielgruppen")
      ]
    },
    {
      id: 5,
      title: t('autocompleteTitle'),
      description: t('autocompleteDescription'),
      icon: "Search",
      image: "https://images.pexels.com/photos/1999463/pexels-photo-1999463.jpeg",
      link: "/services/browser-autocomplete",
      features: [
        tx(currentLanguage, "Intent en temps réel", "Real-time intent", "Echtzeit-Intent"), 
        tx(currentLanguage, "Première impression", "First impression", "Erster Eindruck"), 
        tx(currentLanguage, "Taux de conversion élevé", "High conversion rate", "Hohe Conversion-Rate")
      ]
    },
    {
      id: 6,
      title: t('deepLinkTitle'),
      description: t('deepLinkDescription'),
      icon: "Link",
      image: "https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwbWFya2V0aW5nfGVufDB8fHxibHVlfDE3NjE1NzczMjF8MA&ixlib=rb-4.1.0&q=85",
      link: "/services/deep-link-technology",
      features: [
        tx(currentLanguage, "Technologie propriétaire", "Proprietary technology", "Proprietäre Technologie"), 
        "Deep link bidder", 
        tx(currentLanguage, "Ciblage contextuel", "Contextual targeting", "Kontextuelles Targeting")
      ]
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('servicesTitle')}
            <span className="block text-2xl md:text-3xl lg:text-4xl bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mt-2">
              {t('servicesSubtitle')}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('servicesDescription')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service, index) => {
            const IconComponent = Icons[service.icon];
            
            return (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-gray-50/50 overflow-hidden"
              >
                {/* Service Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent group-hover:from-black/30 transition-all duration-300"></div>
                  
                  {/* Icon overlay */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-all duration-300">
                    <IconComponent className="w-6 h-6" />
                  </div>
                </div>

                <CardHeader className="pb-3">
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                    {tr(currentLanguage, service.title)}
                  </CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {tr(currentLanguage, service.description)}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <Badge 
                        key={featureIndex}
                        variant="secondary" 
                        className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors duration-200"
                      >
                        {tr(currentLanguage, feature)}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Learn More Button */}
                  <button
                    onClick={() => service.link !== "#" ? navigate(`/${currentLanguage}${service.link}`) : null}
                    className={`w-full mt-4 px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                      service.link !== "#" 
                        ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={service.link === "#"}
                  >
                    {tx(currentLanguage, 'En savoir plus', 'Learn More', 'Mehr erfahren')}
                    <Icons.ArrowRight className="w-4 h-4" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-full border border-blue-100">
            <span className="text-gray-700 font-medium">
              {t('customSolution')}
            </span>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="ml-3 text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-200"
            >
              {t('contactUs')} →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;